import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faBoxOpen, faTruckFast } from '@fortawesome/free-solid-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar, WA_NUMBER } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { getStorefrontConfig } from '../lib/dropify'
import { DEFAULT_OG_IMAGE, absoluteUrl, brandTitle, buildFaqSchema } from '../lib/seo'
import { CATEGORY_ORDER_RULES, GEKKO_MINIMUM_LUSIN, MANSHET_MINIMUM_LUSIN, MEDICAL_GLOVE_MINIMUM_BOX } from '../lib/productOrdering'

const SEO_TITLE = 'Ketentuan Minimum Order Grosir per Kategori'
const SEO_DESCRIPTION =
  'Cek minimum order grosir Dropify per kategori, termasuk produk lusinan, sarung tangan medis per box, dan masker sesuai tipe sebelum meminta quotation.'

// Business rule: minimum order berbeda tiap kategori dan satuan transaksi.
type MoqRow = {
  category: string
  href?: string
  minimum: string
  equivalent?: string
}

const MOQ_ROWS: MoqRow[] = [
  { category: 'Bra', href: '/kategori/bra-lingerie', minimum: `${CATEGORY_ORDER_RULES['bra-lingerie'].minimum} lusin`, equivalent: `${CATEGORY_ORDER_RULES['bra-lingerie'].minimum * 12} pcs` },
  { category: 'CD & Underwear', href: '/kategori/cd-underwear', minimum: `${CATEGORY_ORDER_RULES['cd-underwear'].minimum} lusin`, equivalent: `${CATEGORY_ORDER_RULES['cd-underwear'].minimum * 12} pcs` },
  { category: 'Kaos Kaki', href: '/kategori/kaos-kaki', minimum: `${CATEGORY_ORDER_RULES['kaos-kaki'].minimum} lusin`, equivalent: `${CATEGORY_ORDER_RULES['kaos-kaki'].minimum * 12} pasang` },
  { category: 'Sarung Tangan Kerja Umum', href: '/kategori/manshet-sarung-tangan', minimum: `${CATEGORY_ORDER_RULES['manshet-sarung-tangan'].minimum} lusin`, equivalent: `${CATEGORY_ORDER_RULES['manshet-sarung-tangan'].minimum * 12} pasang` },
  { category: 'Sarung Tangan Gekko Grip', href: '/sarung-tangan-gekko', minimum: `${GEKKO_MINIMUM_LUSIN} lusin`, equivalent: `${GEKKO_MINIMUM_LUSIN * 12} pasang` },
  { category: 'Manset', href: '/kategori/manshet-sarung-tangan', minimum: `${MANSHET_MINIMUM_LUSIN} lusin`, equivalent: `${MANSHET_MINIMUM_LUSIN * 12} pcs` },
  { category: 'Sarung Tangan Medis', href: '/perlengkapan-medis', minimum: `${MEDICAL_GLOVE_MINIMUM_BOX} box` },
  { category: 'Masker', href: '/kategori/masker', minimum: 'Konfirmasi per tipe' },
  { category: 'Handuk', href: '/kategori/handuk', minimum: `${CATEGORY_ORDER_RULES.handuk.minimum} lusin`, equivalent: `${CATEGORY_ORDER_RULES.handuk.minimum * 12} pcs` },
]

const FAQ_ITEMS = [
  {
    q: 'Berapa minimum order di Dropify?',
    a: 'Minimum order berbeda tiap kategori dan satuan transaksi. Handuk mulai 10 lusin, kaos kaki 50 lusin, dan sarung tangan medis 10 box. Untuk masker, admin akan mengonfirmasi minimum order berdasarkan tipenya. Rinciannya ada pada tabel di halaman ini.',
  },
  {
    q: 'Kenapa minimum order beda-beda tiap kategori?',
    a: 'Satuan transaksi dan isi kemasan berbeda antarproduk. Gunakan angka pada tabel sebagai acuan awal, lalu konfirmasikan nama produk kepada admin sebelum memesan.',
  },
  {
    q: 'Apakah bisa order di bawah minimum?',
    a: 'Kirim nama produk dan jumlah yang dibutuhkan melalui WhatsApp. Admin akan memeriksa apakah produk tersebut dapat dipesan di bawah ketentuan umum. Jangan menganggap pengecualian berlaku untuk semua produk.',
  },
  {
    q: 'Apakah ada diskon untuk pembelian jumlah besar?',
    a: 'Harga mengikuti produk dan jumlah. Kirim nama produk serta jumlah yang Anda butuhkan agar admin dapat menyiapkan quotation.',
  },
  {
    q: 'Apakah bisa beli kaos kaki karungan di Dropify?',
    a: 'Bisa. Pembelian dalam jumlah besar dilayani melalui WhatsApp. MOQ kaos kaki umumnya 50 lusin. Sampaikan perkiraan jumlah agar admin dapat menghitung konversinya ke lusin dan menyiapkan quotation.',
  },
  {
    q: 'Apa arti lusin, kodi, dan karungan dalam pembelian grosir?',
    a: 'Satu lusin berisi 12 pcs atau pasang dan satu kodi berisi 20 pcs. Karungan berarti pembelian dalam kemasan besar, tetapi isi, jumlah, dan ketentuan campurnya perlu diperiksa per produk. Produk medis dan masker dapat memakai satuan box.',
  },
]

export const Route = createFileRoute('/minimum-order')({
  loader: async () => ({
    config: await getStorefrontConfig().catch(() => null),
  }),
  head: () => {
    const title = brandTitle(SEO_TITLE)
    return {
      meta: [
        { title },
        { name: 'description', content: SEO_DESCRIPTION },
        { name: 'robots', content: 'index, follow' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: SEO_DESCRIPTION },
        { property: 'og:url', content: absoluteUrl('/minimum-order') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: SEO_DESCRIPTION },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/minimum-order') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS.map((f) => ({ q: f.q, a: f.a })))),
        },
      ],
    }
  },
  component: MinimumOrderPage,
})

function CategoryCell({ row }: { row: MoqRow }) {
  if (!row.href) {
    return <span className="font-semibold text-slate-900">{row.category}</span>
  }
  return (
    <a
      href={row.href}
      onClick={() =>
        track('click_internal_link', {
          link_url: row.href,
          link_text: row.category,
          link_location: 'minimum_order',
        })
      }
      className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-2 transition-colors hover:decoration-blue-500"
    >
      {row.category}
    </a>
  )
}

function MinimumOrderPage() {
  const { config } = Route.useLoaderData()
  const waHref = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    'Halo Dropify, saya ingin menanyakan ketentuan minimum order.',
  )}`

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <div className="bg-slate-900 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
            <a href="/" className="hover:text-white transition-colors">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <span className="text-white font-medium">Minimum Order</span>
          </nav>
          <h1 className="text-4xl font-black text-white uppercase tracking-tight mb-2">Minimum Order</h1>
          <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
            Minimum order di Dropify berbeda menurut kategori dan satuan transaksi. Tabel berikut memuat ketentuan umum yang perlu dikonfirmasi kembali untuk produk yang Anda pilih.
          </p>
        </div>
      </div>

      <WhatsAppChannelSection />

      <main className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        {/* MOQ table */}
        <section className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <FontAwesomeIcon icon={faBoxOpen} className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900">Ketentuan minimum order per kategori</h2>
              <p className="text-xs text-slate-500">Minimum order memakai satuan lusin atau box sesuai produk.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3 font-bold">Kategori</th>
                  <th className="px-6 py-3 font-bold">Minimum Order</th>
                </tr>
              </thead>
              <tbody>
                {MOQ_ROWS.map((row) => (
                  <tr key={row.category} className="border-b border-slate-50 last:border-0">
                    <td className="px-6 py-4">
                      <CategoryCell row={row} />
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-slate-900">{row.minimum}</span>
                      {row.equivalent && <span className="ml-2 text-xs text-slate-400">setara {row.equivalent}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Unit glossary — answers karungan/lusin/kodi/kulakan search queries
            with standalone, quotable definitions (AEO) */}
        <section className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8">
          <h2 className="text-xl font-black text-slate-900 tracking-tight mb-3">Arti karungan, kodi, dan lusin</h2>
          <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
            <p>
              <span className="font-semibold text-slate-900">Lusin</span> adalah satuan grosir berisi 12 pcs atau pasang.
            </p>
            <p>
              <span className="font-semibold text-slate-900">Kodi</span> adalah satuan grosir berisi 20 pcs.
            </p>
            <p>
              <span className="font-semibold text-slate-900">Karungan</span> atau bal adalah pembelian dalam kemasan besar. Jumlah dan komposisi di dalamnya perlu dikonfirmasi per produk.
            </p>
            <p>
              <span className="font-semibold text-slate-900">Kulakan</span> adalah istilah umum untuk belanja stok dalam jumlah besar untuk dijual kembali.
            </p>
            <p>
              Sebagian besar kategori umum memakai satuan lusin. Sarung tangan medis dan masker memakai satuan box. Jika Anda terbiasa menghitung dalam kodi atau karung, sampaikan jumlah dan nama produk kepada admin agar dapat dikonversi ke satuan transaksi yang sesuai.
            </p>
          </div>
        </section>

        {/* Notes */}
        <section className="rounded-2xl border border-blue-100 bg-blue-50/60 p-6">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon icon={faTruckFast} className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
            <div className="space-y-2 text-sm leading-relaxed text-slate-700">
              <p>
                <span className="font-semibold text-slate-900">Jumlah di bawah minimum.</span> Kirim nama produk dan jumlah yang dibutuhkan. Admin akan memeriksa apakah pengecualian tersedia untuk produk tersebut.
              </p>
              <p>
                <span className="font-semibold text-slate-900">Membutuhkan jumlah besar?</span> Kirim produk dan jumlah yang dibutuhkan agar admin dapat memeriksa kemampuan pemenuhan dan menyiapkan quotation sesuai volume tersebut.
              </p>
            </div>
          </div>
          <a
            href={waHref}
            {...whatsAppCtaProps('minimum_order', { cta_placement: 'body_primary' })}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-green-700 px-5 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-green-800"
          >
            Tanyakan ketentuan melalui WhatsApp
          </a>
        </section>

        {/* FAQ */}
        <section className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8">
          <h2 className="text-xl font-black text-slate-900 tracking-tight mb-5">Pertanyaan seputar minimum order</h2>
          <div className="space-y-5">
            {FAQ_ITEMS.map((item) => (
              <div key={item.q}>
                <h3 className="text-sm font-bold text-slate-900 mb-1">{item.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer config={config} />
    </div>
  )
}
